// src/components/FoodCartScreen.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import { useFoodCart } from '../store/foodCartState';
import { formatCurrency } from '../utils/helpers';
import {
  FiArrowRight, FiShoppingCart, FiTrash2, FiMinus, FiPlus, FiX,
  FiDollarSign, FiCreditCard, FiPackage, FiCheckCircle
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const FoodCartScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cart = useFoodCart();
  const [ordering, setOrdering] = useState(false);

  const cashTotal = cart.items
    .filter((i) => i.paymentMethod === 'cash')
    .reduce((s, i) => s + (i.service.price || 0) * i.quantity, 0);
  const walletTotal = cart.items
    .filter((i) => i.paymentMethod === 'wallet')
    .reduce((s, i) => s + (i.service.price || 0) * i.quantity, 0);

  const placeOrder = async () => {
    if (cart.items.length === 0) return;
    if (!user) {
      toast.error('يجب تسجيل الدخول لإتمام الشراء');
      return;
    }

    // ✅ تحقق من رصيد المحفظة لعناصر الدفع بالمحفظة
    const walletItems = cart.items.filter((i) => i.paymentMethod === 'wallet');
    if (walletItems.length > 0) {
      const client = await supabaseService.getClientById(user.id);
      const balance = client?.availableBalance ?? 0;
      if (balance < walletTotal) {
        toast.error(`رصيدك الحالي (${balance.toFixed(0)}) غير كافٍ لإتمام الشراء بالمحفظة`);
        return;
      }
    }

    setOrdering(true);
    try {
      for (const item of cart.items) {
        const totalAmount = (item.service.price || 0) * item.quantity;

        const requestId = await supabaseService.createRequest({
          clientId: user.id,
          providerId: item.provider.id,
          serviceId: item.service.id,
          status: 'pending',
          paymentMethod: item.paymentMethod,
          quantity: item.quantity,
        });

        if (item.paymentMethod === 'wallet') {
          await supabaseService.freezeWalletPayment({
            clientId: user.id,
            amount: totalAmount,
            requestId,
          });
        }
      }

      cart.clear();
      toast.success('تم إرسال طلبك بنجاح!');
      navigate('/client');
    } catch (e) {
      console.error('placeOrder error:', e);
      toast.error(`فشل إتمام الطلب: ${e.message || ''}`);
    } finally {
      setOrdering(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6" dir="rtl">
        <FiShoppingCart size={56} className="text-gray-300 mb-4" />
        <p className="text-lg font-bold text-gray-800 mb-1">السلة فارغة</p>
        <p className="text-sm text-gray-400 mb-6">أضف مأكولات من خدمة فود لتظهر هنا</p>
        <button
          onClick={() => navigate('/food-store')}
          className="bg-red-600 text-white px-6 py-2.5 rounded-xl font-bold"
        >
          تصفح المأكولات
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-40" dir="rtl">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 flex items-center justify-between px-4 py-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-gray-100">
          <FiArrowRight />
        </button>
        <h1 className="font-bold text-gray-900 flex items-center gap-2">
          <FiShoppingCart /> سلة المأكولات ({cart.totalCount})
        </h1>
        <button
          onClick={() => cart.clear()}
          className="text-red-500 text-xs font-bold px-2 py-1"
        >
          إفراغ الكل
        </button>
      </div>

      {/* Items */}
      <div className="p-4 space-y-3">
        {cart.items.map((item) => {
          const acceptsWallet = item.provider.acceptsWallet === true;
          return (
            <div
              key={item.service.id}
              className="bg-white rounded-2xl border border-gray-200 p-3 shadow-sm"
            >
              <div className="flex gap-3">
                <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
                  {item.service.imageUrl ? (
                    <img src={item.service.imageUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <FiPackage size={20} />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{item.service.name}</p>
                  <p className="text-[11px] text-gray-400 truncate mb-1">{item.provider.fullName}</p>
                  <p className="text-sm font-black text-gray-900">
                    {formatCurrency
                      ? formatCurrency((item.service.price || 0) * item.quantity)
                      : `${(item.service.price || 0) * item.quantity} د.ج`}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <button
                    onClick={() => cart.removeItem(item.service.id)}
                    className="w-6 h-6 rounded-full bg-red-50 text-red-500 flex items-center justify-center"
                  >
                    <FiX size={13} />
                  </button>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => cart.updateQuantity(item.service.id, item.quantity - 1)}
                      className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-gray-600"
                    >
                      <FiMinus size={11} />
                    </button>
                    <span className="text-sm font-bold w-4 text-center">{item.quantity}</span>
                    <button
                      onClick={() =>
                        item.quantity < (item.service.stock ?? 1) &&
                        cart.updateQuantity(item.service.id, item.quantity + 1)
                      }
                      className={`w-6 h-6 rounded-md flex items-center justify-center ${
                        item.quantity < (item.service.stock ?? 1)
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-300'
                      }`}
                    >
                      <FiPlus size={11} />
                    </button>
                  </div>
                </div>
              </div>

              {/* طريقة الدفع */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => cart.setPaymentMethod(item.service.id, 'cash')}
                  className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5 border transition ${
                    item.paymentMethod === 'cash'
                      ? 'bg-green-50 border-green-500 text-green-600'
                      : 'bg-gray-50 border-gray-200 text-gray-500'
                  }`}
                >
                  <FiDollarSign size={12} /> كاش
                </button>
                <button
                  onClick={() =>
                    acceptsWallet
                      ? cart.setPaymentMethod(item.service.id, 'wallet')
                      : toast.error('هذا المطعم لا يقبل الدفع بالمحفظة')
                  }
                  className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5 border transition ${
                    !acceptsWallet
                      ? 'bg-gray-50 border-gray-200 text-gray-300'
                      : item.paymentMethod === 'wallet'
                      ? 'bg-blue-50 border-blue-500 text-blue-600'
                      : 'bg-gray-50 border-gray-200 text-gray-500'
                  }`}
                >
                  <FiCreditCard size={12} />
                  {acceptsWallet ? 'محفظة' : 'محفظة غير متاحة'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom summary */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 p-4 z-30">
        {cashTotal > 0 && (
          <div className="flex justify-between items-center text-xs mb-1.5">
            <span className="flex items-center gap-1.5 text-gray-500">
              <FiDollarSign className="text-green-500" /> كاش
            </span>
            <span className="font-bold text-green-600">{cashTotal.toFixed(0)} د.ج</span>
          </div>
        )}
        {walletTotal > 0 && (
          <div className="flex justify-between items-center text-xs mb-1.5">
            <span className="flex items-center gap-1.5 text-gray-500">
              <FiCreditCard className="text-blue-500" /> محفظة
            </span>
            <span className="font-bold text-blue-600">{walletTotal.toFixed(0)} د.ج</span>
          </div>
        )}
        <div className="border-t border-gray-100 my-2" />
        <div className="flex justify-between items-center mb-3">
          <span className="font-bold text-gray-900">الإجمالي</span>
          <span className="text-xl font-black text-gray-900">{cart.totalPrice.toFixed(0)} د.ج</span>
        </div>
        <button
          onClick={placeOrder}
          disabled={ordering}
          className="w-full bg-red-600 text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {ordering ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
          ) : (
            <>
              <FiCheckCircle /> إتمام الطلب
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FoodCartScreen;
